
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ChefHat, Heart, Calendar, ShoppingCart, BookOpen, ArrowRight, ArrowLeft, X } from 'lucide-react';
import { useStore } from '@/store/useStore';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  content: React.ReactNode;
}

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const OnboardingModal: React.FC<OnboardingModalProps> = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const updateShowOnboarding = useStore((state) => state.updateShowOnboarding)

  const steps: OnboardingStep[] = [
    {
      id: 'welcome',
      title: 'Welcome to Ezyvibes',
      description: 'Your personal meal planning assistant',
      icon: ChefHat,
      content: (
        <div className="text-center space-y-6">
          <div className="w-24 h-24 mx-auto bg-gradient-to-br from-brand-cyan to-brand-accent rounded-full flex items-center justify-center">
            <ChefHat className="w-12 h-12 text-brand-white" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-brand-white mb-3">Let's get you started!</h3>
            <p className="text-gray-300 leading-relaxed">
              We'll guide you through the key features to help you master meal planning in just a few minutes.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'explore',
      title: 'Explore Recipes',
      description: 'Discover hundreds of delicious recipes',
      icon: BookOpen,
      content: (
        <div className="space-y-6">
          <div className="w-16 h-16 mx-auto bg-brand-cyan/20 rounded-full flex items-center justify-center">
            <BookOpen className="w-8 h-8 text-brand-cyan" />
          </div>
          <div className="text-center">
            <h3 className="text-xl font-bold text-brand-white mb-3">Browse Our Recipe Collection</h3>
            <p className="text-gray-300 mb-4">
              Explore recipes by cuisine, dietary preferences, cooking time, and ingredients. Use our smart search to find exactly what you're craving.
            </p>
            <div className="bg-brand-dark/50 rounded-lg p-4 border border-brand-cyan/20">
              <p className="text-sm text-brand-cyan font-medium">💡 Pro Tip:</p>
              <p className="text-sm text-gray-300 mt-1">Use filters to find recipes that match your dietary needs and available cooking time.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'favorites',
      title: 'Select Favourites',
      description: 'Save recipes you love for quick access',
      icon: Heart,
      content: (
        <div className="space-y-6">
          <div className="w-16 h-16 mx-auto bg-red-500/20 rounded-full flex items-center justify-center">
            <Heart className="w-8 h-8 text-red-500" />
          </div>
          <div className="text-center">
            <h3 className="text-xl font-bold text-brand-white mb-3">Build Your Favourites Collection</h3>
            <p className="text-gray-300 mb-4">
              Heart recipes you love to create a personalized collection. Your favorites will be easily accessible when creating meal templates.
            </p>
            <div className="bg-brand-dark/50 rounded-lg p-4 border border-red-500/20">
              <p className="text-sm text-red-500 font-medium">❤️ Remember:</p>
              <p className="text-sm text-gray-300 mt-1">The more favorites you save, the easier it becomes to create diverse meal plans.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'template',
      title: 'Create Meal Template',
      description: 'Design your perfect weekly meal structure',
      icon: Calendar,
      content: (
        <div className="space-y-6">
          <div className="w-16 h-16 mx-auto bg-brand-accent/20 rounded-full flex items-center justify-center">
            <Calendar className="w-8 h-8 text-brand-accent" />
          </div>
          <div className="text-center">
            <h3 className="text-xl font-bold text-brand-white mb-3">Design Your Meal Template</h3>
            <p className="text-gray-300 mb-4">
              Create a weekly template that fits your lifestyle. Define meal types, portions, and preferences for each day of the week.
            </p>
            <div className="bg-brand-dark/50 rounded-lg p-4 border border-brand-accent/20">
              <p className="text-sm text-brand-accent font-medium">📅 Templates Help:</p>
              <p className="text-sm text-gray-300 mt-1">Save time by reusing your preferred meal structure across multiple weeks.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'plan',
      title: 'Generate 4-Week Plan',
      description: 'Automatically create 4 weeks of meals',
      icon: ChefHat,
      content: (
        <div className="space-y-6">
          <div className="w-16 h-16 mx-auto bg-brand-success/20 rounded-full flex items-center justify-center">
            <ChefHat className="w-8 h-8 text-brand-success" />
          </div>
          <div className="text-center">
            <h3 className="text-xl font-bold text-brand-white mb-3">Generate Your Meal Plan</h3>
            <p className="text-gray-300 mb-4">
              Using your template and favorites, we'll automatically generate a complete 4-week meal plan with variety and balance.
            </p>
            <div className="bg-brand-dark/50 rounded-lg p-4 border border-brand-success/20">
              <p className="text-sm text-brand-success font-medium">🎯 Smart Planning:</p>
              <p className="text-sm text-gray-300 mt-1">Our planning algorithm ensures variety while respecting your preferences and dietary needs.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'shopping',
      title: 'Shopping List',
      description: 'Get organized grocery lists for your plan',
      icon: ShoppingCart,
      content: (
        <div className="space-y-6">
          <div className="w-16 h-16 mx-auto bg-orange-500/20 rounded-full flex items-center justify-center">
            <ShoppingCart className="w-8 h-8 text-orange-500" />
          </div>
          <div className="text-center">
            <h3 className="text-xl font-bold text-brand-white mb-3">Smart Shopping Lists</h3>
            <p className="text-gray-300 mb-4">
              Automatically generated shopping lists organize all ingredients by category and eliminate duplicates across your 4-week plan.
            </p>
            <div className="bg-brand-dark/50 rounded-lg p-4 border border-orange-500/20">
              <p className="text-sm text-orange-500 font-medium">🛒 Efficiency:</p>
              <p className="text-sm text-gray-300 mt-1">Check off items as you shop, and we'll remember your preferences for next time.</p>
            </div>
          </div>
        </div>
      )
    }
  ];

  const progress = ((currentStep + 1) / steps.length) * 100;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      updateShowOnboarding(false);
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md mx-auto bg-brand-black border border-brand-cyan/20 p-0 overflow-hidden">
        <div className="bg-gradient-to-br from-brand-dark to-brand-black">
          {/* Header */}
          <DialogHeader className="p-6 pb-4 relative">
            {/* <Button
              variant="ghost"
              size="sm"
              onClick={handleSkip}
              className="absolute right-4 top-4 text-gray-400 hover:text-brand-white hover:bg-brand-dark/50"
            >
              <X className="w-4 h-4" />
            </Button> */}
            <DialogTitle className="text-brand-white text-lg font-semibold">
              {steps[currentStep].title}
            </DialogTitle>
            <p className="text-gray-300 text-sm">{steps[currentStep].description}</p>
          </DialogHeader>

          {/* Progress Bar */}
          <div className="px-6 pb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-400">Step {currentStep + 1} of {steps.length}</span>
              <span className="text-xs text-brand-cyan">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2 bg-brand-dark">
              <div 
                className="h-full bg-gradient-to-r from-brand-cyan to-brand-accent rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </Progress>
          </div>

          {/* Content */}
          <div className="px-6 pb-6 fade-in-up">
            {steps[currentStep].content}
          </div>

          {/* Footer */}
          <div className="p-6 pt-4 bg-brand-dark/30 border-t border-brand-cyan/10">
            <div className="flex justify-between items-center">
              <Button
                variant="ghost"
                onClick={handlePrevious}
                disabled={currentStep === 0}
                className="text-gray-400 hover:text-brand-white hover:bg-brand-dark/50 disabled:opacity-30"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>

              <div className="flex gap-2">
                {steps.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentStep
                        ? 'bg-brand-cyan'
                        : index < currentStep
                        ? 'bg-brand-cyan/60'
                        : 'bg-gray-600'
                    }`}
                  />
                ))}
              </div>

              <Button
                onClick={handleNext}
                className="bg-brand-cyan hover:bg-brand-cyan/90 text-brand-black font-medium"
              >
                {currentStep === steps.length - 1 ? 'Get Started' : 'Next'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingModal;
